import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, takeUntil, skip, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { MastersService } from '../../services/masters/masters.service';
import { SubsidiaryService } from '../../services/subsidiary/subsidiary.service';
import { DynamicListingComponent, ColumnConfig, ActionConfig, ActionEvent } from '../../dynamic-listing/dynamic-listing';
import { DynamicForm } from '../../dynamic-form/dynamic-form';
import { SVG_ICONS } from '../../utils/svg-icons.utils';
import { ToasterService } from '../../../../../auth/src/lib/toaster.service';

@Component({
  selector: 'lib-chart-of-accounts',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DynamicListingComponent,
    DynamicForm
  ],
  templateUrl: './chart-of-accounts.html',
})
export class ChartOfAccounts implements OnInit, OnDestroy {
  masterType = 'chart-of-accounts';
  isLoading: boolean = false;
  tableData: any[] = [];
  private destroy$ = new Subject<void>();
  
  // Pagination
  pageIndex: number = 1;
  rowsPerPage: number = 10;
  totalCount: number = 0;
  totalPages: number = 0;

  // View mode: 'list' or 'form'
  viewMode: 'list' | 'form' = 'list';
  isEditMode: boolean = false;
  selectedItemId: string | number | null = null;
  showInactives = false;
  formData: any = null;
  formGroup: FormGroup;

  // Options for dropdowns
  accountTypeOptions: any[] = [];
  parentAccountOptions: any[] = [];
  subsidiaryOptions: any[] = [];

  // Column configurations
  columnConfigs: ColumnConfig[] = [
    { key: 'account_code', header: 'Account Code', type: 'text' },
    { key: 'name', header: 'Account Name', type: 'text' },
    { key: 'account_type_name', header: 'Account Type', type: 'text' },
    { key: 'parent_account_name', header: 'Parent Account', type: 'text' },
    { key: 'is_control_account', header: 'Control Account', type: 'status' },
    { key: 'is_active', header: 'Status', type: 'status' },
  ];

  // Action configurations
  actionConfigs: ActionConfig[] = [
    {
      key: 'toggle',
      label: 'Status',
      tooltip: 'Toggle Active/Inactive',
      type: 'toggle'
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT
    },
    {
      key: 'delete',
      label: 'Delete',
      tooltip: 'Delete',
      severity: 'secondary',
      svgIcon: SVG_ICONS.DELETE
    }
  ];

  // Form field configurations
  formFields: any[] = [];

  constructor(
    private mastersService: MastersService,
    private subsidiaryService: SubsidiaryService,
    private toasterService: ToasterService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.formGroup = this.fb.group({});
  }

  async loadMasters(page: number = 1) {
    try {
      this.isLoading = true;

      const params: any = {};
      if (this.showInactives) {
        params.include_inactive = 'true';
      }

      const response: any = await lastValueFrom(
        this.mastersService.getAllAccounts(params)
      );

      // Extract data from response
      const accountsData = response?.data?.accounts || [];
      this.tableData = accountsData.map((account: any) => ({
        ...account,
        account_type_name: account.account_type?.name || account.account_type_name || '-',
        parent_account_name: account.parent_account?.name || '-',
      }));
      this.totalCount = response?.data?.total || this.tableData.length;
      this.totalPages = Math.ceil(this.totalCount / this.rowsPerPage);
      this.pageIndex = page;
    } catch (error) {
      console.error('Error loading chart of accounts:', error);
      this.toasterService.error('Error', 'Failed to load chart of accounts');
    } finally {
      this.isLoading = false;
    }
  }

  async loadDropdownOptions() {
    try {
      // Load account types
      const accountTypesResponse: any = await lastValueFrom(
        this.mastersService.getAccountTypes()
      );
      const accountTypes = accountTypesResponse?.data?.account_types || accountTypesResponse?.data || [];
      this.accountTypeOptions = accountTypes.map((at: any) => ({
        label: at.name || at.display_name,
        value: at.id
      }));

      // Load subsidiaries
      const subsidiariesResponse: any = await lastValueFrom(
        this.subsidiaryService.getSubsidiaries()
      );
      const subsidiaries = subsidiariesResponse?.data?.subsidiaries || [];
      this.subsidiaryOptions = subsidiaries.map((sub: any) => ({
        label: sub.name || sub.display_name,
        value: sub.id
      }));

      // Load parent accounts (all accounts for potential parent selection)
      const accountsResponse: any = await lastValueFrom(
        this.mastersService.getAllAccounts({})
      );
      const accounts = accountsResponse?.data?.accounts || [];
      this.parentAccountOptions = accounts
        .filter((acc: any) => acc.is_control_account) // Only control accounts can be parents
        .map((acc: any) => ({
          label: `${acc.account_code} - ${acc.name}`,
          value: acc.id
        }));
    } catch (error) {
      console.error('Error loading dropdown options:', error);
    }
  }

  async loadItemForEdit(id: string | number) {
    try {
      this.isLoading = true;
      // Load dropdown options first
      await this.loadDropdownOptions();
      
      const response: any = await lastValueFrom(
        this.mastersService.getChartOfAccountById(id)
      );
      
      // Handle different response structures
      this.formData = response?.data?.account || response?.data || response?.account || response;
      
      // Extract subsidiary IDs from the account data
      if (this.formData.subsidiaries && Array.isArray(this.formData.subsidiaries)) {
        this.formData.subsidiary_ids = this.formData.subsidiaries.map((s: any) => s.subsidiary_id || s.id);
      }
      
      this.initializeFormFields();
      this.initializeForm();
      this.isLoading = false;
    } catch (error) {
      console.error('Error loading item for edit:', error);
      this.toasterService.error('Error', 'Failed to load account for editing');
      this.isLoading = false;
    }
  }

  initializeFormFields() {
    this.formFields = [
      {
        field_name: 'account_code',
        display_name: 'Account Code',
        field_type: 'input',
        data_type: 'string',
        is_mandatory: true,
        is_visible: true,
        display_order: 1,
        help_text: 'Unique code for this account (e.g., 1010)',
        is_disabled: this.isEditMode // Account code cannot be changed after creation
      },
      {
        field_name: 'name',
        display_name: 'Account Name',
        field_type: 'input',
        data_type: 'string',
        is_mandatory: true,
        is_visible: true,
        display_order: 2,
        help_text: 'Name of the account'
      },
      {
        field_name: 'account_type_id',
        display_name: 'Account Type',
        field_type: 'select',
        data_type: 'number',
        is_mandatory: true,
        is_visible: true,
        display_order: 3,
        options: this.accountTypeOptions,
        help_text: 'Type of account (Asset, Liability, Revenue, etc.)'
      },
      {
        field_name: 'parent_account_id',
        display_name: 'Parent Account',
        field_type: 'select',
        data_type: 'number',
        is_mandatory: false,
        is_visible: true,
        display_order: 4,
        options: this.parentAccountOptions,
        help_text: 'Parent control account (optional)'
      },
      {
        field_name: 'subsidiary_ids',
        display_name: 'Subsidiaries',
        field_type: 'multiselect',
        data_type: 'array',
        is_mandatory: true,
        is_visible: true,
        display_order: 5,
        options: this.subsidiaryOptions,
        help_text: 'Select at least one subsidiary to map this account (required)'
      },
      {
        field_name: 'is_control_account',
        display_name: 'Is Control Account',
        field_type: 'checkbox',
        data_type: 'boolean',
        is_mandatory: false,
        is_visible: true,
        display_order: 6,
        help_text: 'Control accounts can have sub-accounts'
      },
      {
        field_name: 'allow_manual_entry',
        display_name: 'Allow Manual Entry',
        field_type: 'checkbox',
        data_type: 'boolean',
        is_mandatory: false,
        is_visible: true,
        display_order: 7,
        default_value: true,
        help_text: 'Allow manual journal entries to this account'
      }
    ];
  }

  initializeForm() {
    const formControls: any = {};
    
    this.formFields.forEach(field => {
      const key = field.field_name;
      let value = this.formData?.[key];
      
      // Set default values
      if (value === undefined || value === null) {
        if (field.field_type === 'checkbox') {
          value = field.default_value ?? false;
        } else if (field.field_type === 'multiselect') {
          value = [];
        } else {
          value = field.default_value ?? '';
        }
      }
      
      const validators = field.is_mandatory ? [Validators.required] : [];
      
      // Add minimum length validator for subsidiary_ids (at least 1)
      if (key === 'subsidiary_ids') {
        validators.push(Validators.minLength(1));
      }
      
      formControls[key] = [value, validators];
    });
    
    this.formGroup = this.fb.group(formControls);
  }

  onActionClicked(event: ActionEvent) {
    const { action, row } = event;
    
    switch (action) {
      case 'edit':
        this.editItem(row);
        break;
      case 'toggle':
        this.toggleStatus(row);
        break;
      case 'delete':
        this.deleteItem(row);
        break;
    }
  }

  editItem(row: any) {
    this.router.navigate(['/settings/masters/chart-of-accounts/edit', row.id]);
  }

  async toggleStatus(row: any) {
    const newStatus = Boolean(row.is_active);
    const statusText = newStatus ? 'activate' : 'deactivate';
    
    if (confirm('Are you sure you want to ' + statusText + ' "' + (row.name || row.id) + '"?')) {
      try {
        this.isLoading = true;
        
        const updateData = {
          is_active: newStatus
        };
        
        await lastValueFrom(
          this.mastersService.updateChartOfAccount(row.id, updateData)
        );
        
        this.toasterService.success('Success', `Account ${newStatus ? 'activated' : 'deactivated'} successfully`);
        this.loadMasters(this.pageIndex);
      } catch (error) {
        console.error('Error toggling status:', error);
        this.toasterService.error('Error', 'Failed to toggle status');
      } finally {
        this.isLoading = false;
      }
    }
  }

  async deleteItem(row: any) {
    if (confirm('Are you sure you want to delete "' + (row.name || row.id) + '"?')) {
      try {
        await lastValueFrom(
          this.mastersService.deleteChartOfAccount(row.id)
        );
        this.toasterService.success('Success', 'Account deleted successfully');
        this.loadMasters(this.pageIndex);
      } catch (error) {
        console.error('Error deleting item:', error);
        this.toasterService.error('Error', 'Failed to delete account');
      }
    }
  }

  onPageChanged(page: number) {
    this.loadMasters(page);
  }

  onShowInactivesChange() {
    this.pageIndex = 1;
    this.loadMasters(this.pageIndex);
  }

  onCreateClick() {
    this.router.navigate(['/settings/masters/chart-of-accounts/create']);
  }

  async ngOnInit() {
    await this.checkRouteAndLoad();
    
    let previousUrl = this.router.url;
    
    this.route.params
      .pipe(
        skip(1),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe(async () => {
        await this.checkRouteAndLoad();
      });
    
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(async (event: any) => {
        const currentUrl = event.urlAfterRedirects || event.url;
        if (currentUrl !== previousUrl) {
          previousUrl = currentUrl;
          await this.checkRouteAndLoad();
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async checkRouteAndLoad() {
    const url = this.router.url;
    let routeParams = this.route.snapshot.params;
    let childRoute = this.route.snapshot.firstChild;
    while (childRoute) {
      routeParams = { ...routeParams, ...childRoute.params };
      childRoute = childRoute.firstChild;
    }
    
    let id = routeParams['id'];
    if (!id && url.includes('/edit/')) {
      const match = url.match(/\/edit\/([^\/]+)/);
      if (match) id = match[1];
    }
    if (!id && url.includes('/view/')) {
      const match = url.match(/\/view\/([^\/]+)/);
      if (match) id = match[1];
    }
    
    // Check if we're in create mode
    if (url.includes('/create') || routeParams['action'] === 'create') {
      this.viewMode = 'form';
      this.isEditMode = false;
      this.selectedItemId = null;
      this.formData = { allow_manual_entry: true }; // Default values
      this.isLoading = true;
      await this.loadDropdownOptions();
      this.initializeFormFields();
      this.initializeForm();
      this.isLoading = false;
    }
    // Check if we're in edit mode
    else if (id && (url.includes('/edit') || url.includes('/view'))) {
      this.selectedItemId = id;
      this.isEditMode = url.includes('/edit');
      this.viewMode = 'form';
      await this.loadItemForEdit(id);
    }
    // Default to list mode
    else {
      this.viewMode = 'list';
      this.isEditMode = false;
      this.selectedItemId = null;
      this.loadMasters();
    }
  }

  onFormFieldChanged(event: any) {
    if (!this.formData) {
      this.formData = {};
    }
    this.formData[event.key] = event.value;
  }

  async onFormSubmit() {
    // Validate form
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      
      // Check specific validation for subsidiary_ids
      const subsidiaryIds = this.formGroup.get('subsidiary_ids')?.value;
      if (!subsidiaryIds || subsidiaryIds.length === 0) {
        this.toasterService.error('Validation Error', 'At least one subsidiary must be selected');
        return;
      }
      
      this.toasterService.error('Validation Error', 'Please fill in all required fields');
      return;
    }

    try {
      this.isLoading = true;
      const formValue = this.formGroup.value;
      
      // Ensure subsidiary_ids is an array
      if (!formValue.subsidiary_ids || formValue.subsidiary_ids.length === 0) {
        this.toasterService.error('Validation Error', 'At least one subsidiary must be selected');
        this.isLoading = false;
        return;
      }
      
      if (this.isEditMode && this.selectedItemId) {
        // For edit, don't send account_code as it cannot be changed
        const updateData = {
          name: formValue.name,
          parent_account_id: formValue.parent_account_id || null,
          is_active: formValue.is_active ?? true,
          allow_manual_entry: formValue.allow_manual_entry ?? true
        };
        
        await lastValueFrom(
          this.mastersService.updateChartOfAccount(this.selectedItemId, updateData)
        );
        this.toasterService.success('Success', 'Account updated successfully');
      } else {
        // For create, include all fields including subsidiary_ids
        const createData = {
          account_code: formValue.account_code,
          name: formValue.name,
          account_type_id: formValue.account_type_id,
          parent_account_id: formValue.parent_account_id || null,
          subsidiary_ids: formValue.subsidiary_ids,
          is_control_account: formValue.is_control_account ?? false,
          allow_manual_entry: formValue.allow_manual_entry ?? true
        };
        
        await lastValueFrom(
          this.mastersService.createChartOfAccount(createData)
        );
        this.toasterService.success('Success', 'Account created successfully');
      }
      
      this.router.navigate(['/settings/masters/chart-of-accounts']);
    } catch (error: any) {
      console.error('Error saving account:', error);
      const errorMessage = error?.error?.message || error?.message || 'Failed to save account';
      this.toasterService.error('Error', errorMessage);
    } finally {
      this.isLoading = false;
    }
  }

  onCancel() {
    this.router.navigate(['/settings/masters/chart-of-accounts']);
  }
}

